import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { authSelectors } from 'app-components/flux/auth';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { userInfoActions, userInfoSelectors } from 'app-components/flux/user-info';
import { platform } from 'app-components/utils/core';

import { useFetchOnce } from 'app-components/hooks/utils';

// Validate that user is entitled to run Simplifi. We use the userinfo vs entitlements to handle
// shared datasets. A user may not be entitled (no subscription) but may have access to a
// shared dataset. In this case, a user is provisionally entitled and entitlements will come from
// the owner of the shared dataset (so selected dataset is required before we can get the
// actual entitlements.
//
// If the user is not entitled to run Simplifi, then we redirect to the "pick a plan" page.
//
const useEntitlementValidator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isChromebook, setIsChromebook] = useState(false);

  const authSession = useSelector(authSelectors.getAuthSession);
  const userInfo = useSelector(userInfoSelectors.getUserInfo);
  const isEntitled = useSelector(userInfoSelectors.isEntitled);

  const playBillingFeatureFlag = useSelector(featureFlagsSelectors.getFeatureFlags)?.get('playBilling');
  const isPlayBillingEnabled = playBillingFeatureFlag && isChromebook;

  // fetch userinfo so we can check whether user is entitled to run Simplifi
  useFetchOnce(userInfoActions.getUserInfo, userInfoSelectors, false, true);

  useEffect(() => {
    (async () => {
      const chromebook = await platform.isChromebook();
      setIsChromebook(chromebook);
    })();
  }, []);

  useEffect(() => {
    if (userInfo && !isEntitled) {
      if (isPlayBillingEnabled) {
        navigate('/pick-your-plan');
      } else {
        navigate('/no-subscription');
      }
    }
  }, [authSession, navigate, isEntitled, isPlayBillingEnabled, userInfo, dispatch]);
};

export default useEntitlementValidator;
